import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAuth } from '@gbs-monorepo-packages/auth';
import {
  BaseTooltip,
  FormBase,
  FormModal,
  HighUserRoles,
  type IBaseModalProps,
  Roles,
  SelectData,
  TemplatesType,
  groupsTypes,
  isTruncatedString,
  truncateString,
} from '@gbs-monorepo-packages/common';
import {
  type InsertCourseSchema,
  insertCourseSchema,
} from '@gbs-monorepo/edge/formSchemas/courseSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import LoadingSpinnerAnimated from '../../../../assets/spinner.svg';
import {
  type ITemplateGroupDTO,
  getTemplateGroupsByClientId,
} from '../../../../services/templates';
import Logger from '../../../../utils/logger';
import { Center, Loading, SelectContainer, TruncatedText } from './styles';

interface IAddPageModalProps extends Partial<
  Omit<IBaseModalProps, 'children' | 'onOpenChange'>
> {
  clientId: number;
  onAccept: (course: InsertCourseSchema) => Promise<void>;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
}

interface ITemplateGroupSelectData {
  key: number;
  value: JSX.Element | string;
}

const MAX_TEMPLATE_GROUP_TITLE_CHARS = 35;

const templatesOptionsFiltered = groupsTypes.filter(
  (i) => i.key !== TemplatesType.BUILD_SCRATCH
);

const templateOptions = groupsTypes.filter(
  (i) =>
    i.key === TemplatesType.GLOBAL_TEMPLATES ||
    i.key === TemplatesType.MY_TEMPLATES ||
    i.key === TemplatesType.SHARE_TEMPLATES
);

const defaultOption = TemplatesType.MY_TEMPLATES;

export const AddCourseWithCoursesTemplateModal = ({
  clientId,
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  zIndex,
  ...props
}: IAddPageModalProps): JSX.Element | null => {
  const isSelectOpen = useRef(new Set());
  const [templateOptionsId, setTemplateOptionsId] =
    useState<number>(defaultOption);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templatesGroups, setTemplatesGroups] = useState<
    ITemplateGroupSelectData[]
  >([]);
  const [loading, setLoading] = useState(false);
  const { user, getCurrentRole } = useAuth();
  const currentRole = getCurrentRole(user?.roles ?? ['']);

  const createCourse = useForm<InsertCourseSchema>({
    resolver: zodResolver(insertCourseSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      title: '',
      templateGroupId: 0,
    },
  });

  const {
    reset,
    formState: { errors, isValid },
    watch,
    handleSubmit,
    setValue,
  } = createCourse;

  const values = watch();

  const resetForm = useCallback(() => {
    reset({
      title: '',
      templateGroupId: 0,
    });
    setTemplateOptionsId(defaultOption);
  }, [reset]);

  const onSubmit: SubmitHandler<InsertCourseSchema> = useCallback(
    async (data: InsertCourseSchema) => {
      setLoading(true);
      try {
        const courseAux: InsertCourseSchema = {
          templateGroupId: data.templateGroupId,
          title: data.title,
        };
        await onAccept(courseAux);
        resetForm();
      } catch (error) {
        Logger.debug('error: ', error);
      } finally {
        setLoading(false);
      }
    },
    [onAccept, resetForm]
  );

  const handleDeclineAddPage = useCallback(() => {
    if (!isSelectOpen.current.size) {
      resetForm();
      onDecline?.();
    }
  }, [onDecline, resetForm]);

  const templateTypesOptions = useMemo(() => {
    if (!currentRole) {
      return templateOptions;
    }

    const isAdmin = currentRole.key === Roles.ADMIN;
    const isHighUser = HighUserRoles.includes(currentRole.key) && !isAdmin;

    if (isHighUser) {
      return templateOptions;
    }
    if (isAdmin) {
      return templatesOptionsFiltered;
    }
    return templateOptions;
  }, [currentRole]);

  useEffect(() => {
    if (open) {
      resetForm();
      setTemplatesGroups([]);
    }
  }, [open, resetForm]);

  useEffect(() => {
    let mount = true;

    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const result = await getTemplateGroupsByClientId({
          filter: '',
          limit: 99999,
          page: 1,
          templateOptionsId,
        });
        if (mount) {
          const templatesGroup = result.data.map((group: ITemplateGroupDTO) => {
            const needsTruncate = isTruncatedString(
              group.title,
              MAX_TEMPLATE_GROUP_TITLE_CHARS
            );

            return {
              key: group.id,
              value: needsTruncate ? (
                <BaseTooltip
                  message={group.title}
                  dataCy="tooltip-template-name"
                >
                  <TruncatedText>
                    {truncateString(
                      group.title,
                      MAX_TEMPLATE_GROUP_TITLE_CHARS
                    )}
                  </TruncatedText>
                </BaseTooltip>
              ) : (
                group.title
              ),
            };
          });

          setTemplatesGroups(templatesGroup);
        }
      } catch (err) {
        Logger.debug('err: ', err);
      } finally {
        setTimeout(() => {
          setLoadingTemplates(false);
        }, 200);
      }
    };

    if (clientId && open) {
      void loadTemplates();
      Logger.notice('loadTemplates');
    }

    return () => {
      mount = false;
    };
  }, [templateOptionsId, clientId, open]);

  const handleChangeTemplateType = useCallback(
    (templateType: number) => {
      setValue('templateGroupId', 0, { shouldValidate: true });
      setTemplateOptionsId(templateType);
    },
    [setValue]
  );

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const handleTemplateGroup = useCallback(
    (id: string) => {
      setValue('templateGroupId', +id, { shouldValidate: true });
    },
    [setValue]
  );

  return !open ? null : (
    <FormBase.Provider {...createCourse}>
      <FormModal
        dataCy="title-courseTemplate"
        {...props}
        open={!waitToOpen}
        acceptText="Create course"
        declineText="Cancel"
        mainText="Create from course template"
        onAccept={handleSubmit(onSubmit)}
        onDecline={handleDeclineAddPage}
        onOpenChange={handleDeclineAddPage}
        zIndex={zIndex}
        disabled={!isValid || loading}
        loading={loading}
      >
        <FormBase.Content>
          <FormBase.InputContent
            dataCy="label-courseName"
            filled={!!values.title}
            inputRef="title"
            label="Course Name"
            errorMessage={errors.title?.message}
          >
            <FormBase.InputText
              autoComplete="off"
              dataCy="input-courseName"
              name="title"
              id="title"
              value={values.title}
              required
              type="text"
            />
          </FormBase.InputContent>
          <SelectContainer data-cy="container-group-type">
            <FormBase.InputContent
              dataCy="label-courseTemplateType"
              filled
              inputRef="group-type"
              label="Course Template Type"
              value={templateOptionsId >= 0 ? 'template' : undefined}
              isSelectData
            >
              <SelectData
                defaultValue={String(templateOptionsId)}
                dataCy="select-templateType"
                data={templateTypesOptions}
                name="group-type"
                placeholder="Choose a template type"
                onValueChange={(id) => {
                  handleChangeTemplateType(+id);
                }}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'options');
                }}
                required
                zIndex={10}
                position={
                  templatesGroups.length > 10 ? 'item-aligned' : 'popper'
                }
              />
            </FormBase.InputContent>
          </SelectContainer>
          {loadingTemplates ? (
            <Center>
              <Loading src={LoadingSpinnerAnimated} />
            </Center>
          ) : templatesGroups.length ? (
            <SelectContainer data-cy="template-container">
              <FormBase.InputContent
                dataCy="label-templateType"
                filled={!!values.templateGroupId}
                inputRef="template-type"
                label="Template Type"
                value={values.templateGroupId ? 'type' : undefined}
                isSelectData
              >
                <SelectData
                  data={templatesGroups}
                  dataCy="select-courseTemplate"
                  placeholder="Choose a course template"
                  value={
                    values.templateGroupId
                      ? String(values.templateGroupId)
                      : undefined
                  }
                  onValueChange={handleTemplateGroup}
                  onOpenChange={(isOpen) => {
                    handleOpenChange(isOpen, 'options');
                  }}
                  required
                  zIndex={10}
                  name="choose-group"
                />
              </FormBase.InputContent>
            </SelectContainer>
          ) : (
            <p data-cy="text-noGroupTemplates">No course templates found</p>
          )}
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
